import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { PropertyVideoPhone } from "@/components/public/property-video-phone"
import { DistributionDiagram } from "@/components/public/distribution-diagram"
import {
  EditorialStyles,
  FilmGrain,
  OrnamentRule,
  ScrollProgress,
  SectionEyebrow,
} from "@/components/public/editorial"
import { useEditorialReveal } from "@/hooks/use-editorial-reveal"
import { REAL_EXAMPLES, type RealExample } from "@/data/real-examples"
import type { SeoMeta } from "@/types"

// Picked once per JS bundle load — re-randomises on every hard refresh so
// each visit to the home page features a different real listing in the hero
// and the distribution diagram. Lives at module scope so React's purity
// rules aren't tripped by calling Math.random during render.
const HERO_EXAMPLE: RealExample =
  REAL_EXAMPLES[Math.floor(Math.random() * REAL_EXAMPLES.length)]

const STATS = [
  { value: "15s", label: "Video length" },
  { value: "4", label: "Channels live" },
  { value: "1", label: "Click to publish" },
  { value: "5min", label: "Listing to live" },
]

const PAIN_POINTS = [
  "Same photos. Same floorplan. Same portal listing nobody remembers. Your competitors have the same tools you do, and most of you are using them the same way.",
  "Hiring a videographer once cost £400 per property and took three days. The result felt like a corporate explainer. You haven't booked one since.",
  "Every Sunday night you promise yourself: more videos this week. By Wednesday you're back to the same listing carousel because there's no time.",
]

const STEPS = [
  {
    n: "I",
    title: "Paste",
    body: "Drop a listing URL from Rightmove, Zoopla, OnTheMarket, or your own agency website. We pull the photos, the price, the address, and the description automatically. Nothing to upload.",
  },
  {
    n: "II",
    title: "Render",
    body: "Pick one of four templates (Classic, Urban, Family, Prestige). A cinematic 15-second video is rendered in your brand colours, with voiceover, music, and captions baked in. Square, vertical, and landscape, all at once.",
  },
  {
    n: "III",
    title: "Publish everywhere",
    body: "One click pushes the right cut to Instagram (Reels and feed), Facebook (page and groups), TikTok, and YouTube Shorts. Schedule it for Tuesday morning, post it now, or download every aspect ratio. You never leave the dashboard.",
  },
]

const ADVANTAGES = [
  {
    title: "Posted to four channels in one click.",
    body: "QuiteRight isn't a video maker with a download button. It's a publishing pipeline. Instagram, Facebook, TikTok, YouTube. Connect your accounts once, and every new listing goes live across the lot in seconds.",
  },
  {
    title: "Fill your viewing diary.",
    body: "Video ads on the platforms buyers actually scroll through put your listings in front of people who would never have found them on a portal. More eyes, more enquiries, more viewings booked. Every single week.",
  },
  {
    title: "Get to offer faster.",
    body: "Buyers who've already watched a video walkthrough arrive at viewings ready to act. Fewer tyre-kickers, more serious offers, shorter time on market. Your vendors will notice.",
  },
  {
    title: "Look like a marketing powerhouse.",
    body: "Cinematic video ads on every platform. You look like an agency with a full production team and a social media manager on staff. Win the instruction before your competitor even calls back.",
  },
  {
    title: "Charge what you're worth.",
    body: "Premium marketing sells premium fees. Vendors pay more when they can see the difference between you and the agent up the road.",
  },
  {
    title: "Cheap enough for every listing.",
    body: "Polished enough for a £1.8m townhouse, cheap enough for a £180,000 terrace. The same tool, the same minute, on every property in your portfolio.",
  },
  {
    title: "Locked to your brand.",
    body: "Every video is on-brand by default. Make one between viewings on your phone and it still looks like the design team did it.",
  },
]

const OBJECTIONS = [
  {
    q: "I don't have time for this.",
    a: "It takes less time than writing a portal description. Paste a URL, pick a template, click publish. About five minutes for the first one, two minutes once you've found the template you like.",
  },
  {
    q: "I already post my videos manually.",
    a: "Then you know how long it takes to crop a video for Reels, re-upload for TikTok, write four captions, and schedule everything in Buffer. QuiteRight does that whole loop in one click. The video, the captions, the right aspect ratios, posted to every connected channel at once.",
  },
  {
    q: "Video is expensive.",
    a: "A professional videographer costs £200 to £500 per property. QuiteRight costs a fraction of that, with unlimited videos on every paid plan, and the publishing built in.",
  },
  {
    q: "My listings already do fine.",
    a: "They do. But your competitor just started posting video ads on Instagram, Facebook, TikTok, and YouTube. The agent who moves first wins the vendor's attention and the next instruction.",
  },
  {
    q: "I'm not technical.",
    a: "If you can copy and paste a URL, you can use QuiteRight. Nothing to install, no editing software to learn, no uploads required.",
  },
]

const AUDIENCES = [
  {
    label: "Independent agents",
    body: "Compete with the chains without the budget. Fill your viewing diary like a top-five agency, with marketing they can't match.",
    href: "/for/independent-agents",
  },
  {
    label: "Multi-branch agencies",
    body: "Roll out consistent, on-brand video marketing across every branch. More viewings per listing, every office, from day one.",
    href: "/for/multi-branch",
  },
  {
    label: "Letting agents",
    body: "Rental listings move fast. Video gets viewings booked before the competition even goes live. Fill vacancies quicker, impress landlords.",
    href: "/for/letting-agents",
  },
]

const RESOURCES = [
  {
    label: "Examples",
    body: "Real listings turned into video, before-and-after.",
    href: "/examples",
  },
  {
    label: "How it works",
    body: "The whole product, in one short page.",
    href: "/how-it-works",
  },
  {
    label: "Pricing",
    body: "Transparent plans for solo agents and full agencies.",
    href: "/pricing",
  },
  {
    label: "Agent directory",
    body: "Browse and compare estate agents in Bristol, Bath, Brighton, and Oxford.",
    href: "/directory",
  },
]

const PORTALS = [
  { name: "Rightmove", href: "https://www.rightmove.co.uk" },
  { name: "Zoopla", href: "https://www.zoopla.co.uk" },
  { name: "OnTheMarket", href: "https://www.onthemarket.com" },
  { name: "Airbnb", href: "https://www.airbnb.co.uk" },
  { name: "Booking.com", href: "https://www.booking.com" },
]

/* ------------------------------------------------------------------ */
/* Shared CTA — primary + secondary, used on every cinematic stage     */
/* ------------------------------------------------------------------ */

interface DualCtaProps {
  tone?: "light" | "dark"
  primaryHref?: string
  primaryLabel?: string
  secondaryHref?: string
  secondaryLabel?: string
}

function DualCta({
  tone = "light",
  primaryHref = "/sign-up",
  primaryLabel = "Try it free",
  secondaryHref = "/demo",
  secondaryLabel = "See it with your listings",
}: DualCtaProps) {
  const microcopy = tone === "dark" ? "text-stone/70" : "text-warm-grey"
  const secondaryClasses =
    tone === "dark"
      ? "border border-cream/25 bg-transparent text-cream hover:bg-cream/10"
      : "border border-charcoal/15 bg-transparent text-charcoal hover:bg-charcoal/5"

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
        <Link
          href={primaryHref}
          data-cta-pulse
          className="group relative inline-flex items-center justify-center overflow-hidden rounded-none bg-red px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-18px_rgba(196,30,42,0.65)] transition-all duration-300 hover:bg-deep-red hover:shadow-[0_22px_50px_-15px_rgba(196,30,42,0.8)]"
        >
          <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/15 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-90" />
          <span className="relative">{primaryLabel}</span>
          <span aria-hidden className="relative ml-3 transition-transform duration-300 group-hover:translate-x-1">
            →
          </span>
        </Link>
        <Link
          href={secondaryHref}
          className={`inline-flex items-center justify-center rounded-none px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${secondaryClasses}`}
        >
          {secondaryLabel}
        </Link>
      </div>
      <p className={`text-center text-[11px] font-medium uppercase tracking-[0.18em] ${microcopy}`}>
        Free first video · No card · Cancel any time
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Hero — cinematic charcoal stage with the real video                 */
/* ------------------------------------------------------------------ */

function HeroStage({ example }: { example: RealExample }) {
  return (
    <section className="relative -mt-px overflow-hidden bg-charcoal text-white" data-reveal data-public-hero>
      {/* Atmospheric vignette + soft red spotlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 75% 60% at 50% 40%, rgba(196,30,42,0.18) 0%, rgba(196,30,42,0.05) 32%, rgba(26,26,26,0) 65%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)",
        }}
      />
      <FilmGrain />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-6xl px-6 pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="grid items-center gap-16 md:grid-cols-[1.1fr_1fr] md:gap-20">
          {/* Copy column */}
          <div className="text-center md:text-left">
            <div data-reveal-stagger="1">
              <SectionEyebrow label="Property video marketing" />
            </div>

            <h1
              data-reveal-stagger="2"
              className="mt-8 font-display text-[44px] font-bold leading-[1.04] tracking-tight text-cream sm:text-[60px] md:text-[72px]"
            >
              More viewings.
              <br />
              <span className="italic text-stone/85">Every</span> single
              <br />
              week
              <span className="text-red">.</span>
            </h1>

            <p
              data-reveal-stagger="3"
              className="mx-auto mt-7 max-w-md text-[15px] leading-relaxed text-stone/80 sm:text-base md:mx-0 md:max-w-lg"
            >
              QuiteRight turns any property listing into a cinematic 15-second video ad and
              posts it to Instagram, Facebook, TikTok, and YouTube in one click. Paste any
              UK listing URL. No filming, no editing, no scheduling tools, no fuss.
            </p>

            <div data-reveal-stagger="4" className="mt-10 flex justify-center md:justify-start">
              <DualCta
                tone="dark"
                primaryLabel="Try it free"
                secondaryLabel="Watch a demo"
              />
            </div>
          </div>

          {/* Phone column — real product video */}
          <div data-reveal-stagger="5" className="relative flex justify-center">
            {/* Halo glow underneath the phone */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-x-12 -inset-y-10"
              style={{
                background:
                  "radial-gradient(ellipse 65% 55% at 50% 55%, rgba(196,30,42,0.32) 0%, rgba(196,30,42,0.08) 35%, transparent 72%)",
                filter: "blur(24px)",
              }}
            />
            <div className="relative">
              <PropertyVideoPhone
                videoUrl={example.videoUrl}
                poster={example.videoPoster}
                label={`QuiteRight video for ${example.property}, listed by ${example.agency}`}
                width={280}
              />
            </div>
          </div>
        </div>

        {/* Listing caption — full width, anchored bottom */}
        <div data-reveal-stagger="6" className="mt-16 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-gold">
            A real listing, on QuiteRight
          </p>
          <p className="mx-auto mt-3 max-w-md text-[13px] leading-relaxed text-stone/75">
            {example.property}, {example.location}. {example.beds} bed, {example.price}. Listed by{" "}
            <a
              href={example.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-cream underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold"
            >
              {example.agency}
            </a>
            . Made from the listing photos in a few minutes. Tap the speaker to hear it.
          </p>
        </div>
      </div>

      <div aria-hidden className="absolute inset-x-0 bottom-[6px] h-px bg-stone/15" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Stat strip — auction catalogue specifications plate                 */
/* ------------------------------------------------------------------ */

function StatStrip() {
  return (
    <section className="bg-cream py-12" data-reveal>
      <div className="mx-auto max-w-4xl px-6">
        <div className="grid grid-cols-2 gap-y-10 sm:grid-cols-4 sm:gap-y-0">
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center text-center ${
                i > 0 ? "sm:border-l sm:border-stone" : ""
              }`}
            >
              <span className="font-display text-[40px] font-bold leading-none tracking-tight text-charcoal sm:text-[52px]">
                {stat.value}
                <span className="text-red">.</span>
              </span>
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-warm-grey">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Pain agitation                                                      */
/* ------------------------------------------------------------------ */

function PainSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="The reality" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Your viewing diary
          <br />
          shouldn&rsquo;t have gaps
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-center text-[15px] leading-[1.7] text-slate sm:text-[16px]">
          Same photos. Same floorplan. Same portal listing nobody remembers. Your competitors
          have the same tools you do, but the agencies filling their viewing diaries in 2026
          are the ones marketing differently.
        </p>

        <div className="mt-16 space-y-12">
          {PAIN_POINTS.map((point, i) => {
            const numerals = ["I", "II", "III"]
            return (
              <div key={i} className="flex gap-6">
                <span className="flex-shrink-0 font-display text-[42px] font-bold italic leading-none text-red sm:text-[52px]">
                  {numerals[i]}
                </span>
                <div className="flex-1">
                  <div className="mt-2 h-px w-12 bg-charcoal/20" />
                  <p className="mt-4 text-[16px] leading-[1.7] text-slate sm:text-[17px]">{point}</p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-20">
          <OrnamentRule />
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Distribution stage — visual diagram of photos → video → channels    */
/* ------------------------------------------------------------------ */

function DistributionStage({ example }: { example: RealExample }) {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <SectionEyebrow label="Listing in. Posts out." />
          <h2 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
            Not a video maker.
            <br />
            A publishing pipeline
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-[1.7] text-slate sm:text-[16px]">
            Most tools stop at &ldquo;here&rsquo;s your video, good luck&rdquo;. QuiteRight
            takes the photos from your listing, renders the cinematic ad, then publishes the
            right cut to every channel that matters. No downloads, no re-uploads, no manual
            scheduling.
          </p>
        </div>

        <div className="mt-16">
          <DistributionDiagram example={example} />
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* How it works                                                        */
/* ------------------------------------------------------------------ */

function HowItWorksSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="How it works" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Three steps.
          <br />
          No learning curve
          <span className="text-red">.</span>
        </h2>

        <ol className="mt-16 space-y-10">
          {STEPS.map((step) => (
            <li
              key={step.n}
              className="group relative flex gap-6 border-b border-charcoal/10 pb-10 last:border-b-0 last:pb-0"
            >
              <span className="flex-shrink-0 font-display text-[64px] font-bold italic leading-none text-charcoal/85 transition-colors group-hover:text-red sm:text-[72px]">
                {step.n}
              </span>
              <div className="flex-1 pt-2">
                <p className="font-display text-[22px] font-bold text-charcoal sm:text-[26px]">
                  {step.title}
                  <span className="text-red">.</span>
                </p>
                <p className="mt-3 text-[15px] leading-[1.7] text-slate sm:text-[16px]">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Advantages — six numbered editorial benefits                        */
/* ------------------------------------------------------------------ */

function AdvantagesSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="Marketing superpowers" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          A full marketing team.
          <br />
          Without the headcount
          <span className="text-red">.</span>
        </h2>

        <ul className="mt-16 space-y-10">
          {ADVANTAGES.map((advantage, i) => {
            const num = String(i + 1).padStart(2, "0")
            return (
              <li
                key={advantage.title}
                className="group relative grid grid-cols-[auto_1fr] gap-6"
              >
                <div className="flex flex-col items-center">
                  <span className="font-display text-[28px] font-bold leading-none text-red transition-transform duration-300 group-hover:scale-105">
                    {num}
                  </span>
                  <span className="mt-3 h-full w-px flex-1 bg-charcoal/15" />
                </div>
                <div className="pb-2 pt-1">
                  <p className="font-display text-[22px] font-bold text-charcoal sm:text-[24px]">
                    {advantage.title}
                  </p>
                  <p className="mt-2 text-[15px] leading-[1.7] text-slate sm:text-[16px]">
                    {advantage.body}
                  </p>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Mid CTA stage — cinematic break                                     */
/* ------------------------------------------------------------------ */

function MidCtaStage() {
  return (
    <section className="relative overflow-hidden bg-charcoal text-white" data-reveal>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(196,30,42,0.16) 0%, rgba(196,30,42,0.04) 35%, transparent 70%)",
        }}
      />
      <FilmGrain />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 py-20 text-center md:py-28">
        <SectionEyebrow label="One on the house" tone="gold" />
        <h2 className="mt-6 font-display text-[36px] font-bold leading-[1.1] tracking-tight text-cream sm:text-[48px]">
          Try it on your most
          <br />
          expensive listing
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-stone/80 sm:text-base">
          Pick the property you most want to win. Paste the URL. See what it looks like as a
          video before you commit a penny.
        </p>
        <div className="mt-12 flex justify-center">
          <DualCta tone="dark" primaryLabel="Try it free" secondaryLabel="Watch a demo" />
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Objections — Q. exhibits                                            */
/* ------------------------------------------------------------------ */

function ObjectionsSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="For the sceptics" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Built for busy agents
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-center text-[15px] leading-[1.7] text-slate sm:text-[16px]">
          We know you don&rsquo;t have time for another tool. That&rsquo;s the point.
        </p>

        <dl className="mt-16">
          {OBJECTIONS.map((item, i) => (
            <details
              key={i}
              className="group border-b border-charcoal/10 py-7 last:border-b-0"
              open={i === 0}
            >
              <summary className="flex cursor-pointer items-start gap-5 text-left [&::-webkit-details-marker]:hidden">
                <span className="mt-1 font-display text-[26px] font-bold italic leading-none text-red transition-transform duration-300 group-open:scale-110">
                  Q.
                </span>
                <dt className="flex-1 font-display text-[18px] font-bold leading-snug text-charcoal sm:text-[20px]">
                  &ldquo;{item.q}&rdquo;
                </dt>
                <span
                  aria-hidden
                  className="mt-2 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-charcoal/20 text-charcoal/60 transition-all duration-300 group-open:rotate-45 group-open:border-red group-open:text-red"
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                    <path d="M8 3v10M3 8h10" />
                  </svg>
                </span>
              </summary>
              <dd className="mt-5 pl-12 text-[14px] leading-[1.8] text-slate sm:text-[15px]">
                {item.a}
              </dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Audiences — three persona cards with editorial treatment            */
/* ------------------------------------------------------------------ */

function AudiencesSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <SectionEyebrow label="Who it's for" tone="gold" />
          <h2 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
            From independents
            <br />
            to national brands
            <span className="text-red">.</span>
          </h2>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {AUDIENCES.map((audience) => {
            return (
              <Link
                key={audience.label}
                href={audience.href}
                className="group relative bg-white p-8 shadow-[0_20px_60px_-30px_rgba(26,26,26,0.35)] transition-all duration-300 hover:shadow-[0_30px_80px_-25px_rgba(26,26,26,0.5)] md:p-10"
              >
                <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-gold transition-colors duration-300 group-hover:bg-red" />
                <div aria-hidden className="absolute inset-x-0 top-[5px] h-px bg-gold/40" />

                <h3 className="mt-2 font-display text-[22px] font-bold leading-tight text-charcoal sm:text-[24px]">
                  {audience.label}
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-4 text-[14px] leading-[1.75] text-slate">{audience.body}</p>

                <p className="mt-8 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal transition-colors group-hover:text-red">
                  Read more
                  <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </p>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Portals — hairline strip with outbound links                        */
/* ------------------------------------------------------------------ */

function PortalsStrip() {
  return (
    <section className="bg-white px-6 py-16" data-reveal>
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <SectionEyebrow label="Works with the portals you use" />
          <h2 className="mt-6 font-display text-[24px] font-bold leading-snug tracking-tight text-charcoal sm:text-[28px]">
            Import from any UK property platform
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[14px] leading-[1.7] text-slate">
            Paste a listing URL from any supported portal, or your own agency website. The
            photos, price, address, and description are imported automatically.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {PORTALS.map((portal) => (
            <a
              key={portal.name}
              href={portal.href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-display text-[18px] italic text-charcoal/80 underline decoration-stone underline-offset-[6px] transition-colors hover:text-red hover:decoration-red"
              title={`Visit ${portal.name}`}
            >
              {portal.name}
            </a>
          ))}
        </div>

        <div className="mt-12">
          <OrnamentRule />
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Resources — internal links for SEO and discoverability              */
/* ------------------------------------------------------------------ */

function ResourcesSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-24" data-reveal>
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <SectionEyebrow label="Further reading" />
          <h2 className="mt-6 font-display text-[28px] font-bold leading-tight tracking-tight text-charcoal sm:text-[34px]">
            Resources for estate agents
            <span className="text-red">.</span>
          </h2>
        </div>

        <ul className="mt-12 grid gap-6 sm:grid-cols-2">
          {RESOURCES.map((resource, i) => {
            const num = String(i + 1).padStart(2, "0")
            return (
              <li key={resource.href}>
                <Link
                  href={resource.href}
                  className="group flex items-start gap-5 border-b border-charcoal/10 py-5 transition-colors hover:border-red"
                >
                  <span className="font-display text-[24px] font-bold italic leading-none text-charcoal/40 transition-colors group-hover:text-red">
                    {num}
                  </span>
                  <div className="flex-1">
                    <p className="font-display text-[18px] font-bold text-charcoal transition-colors group-hover:text-red sm:text-[20px]">
                      {resource.label}
                      <span className="text-red">.</span>
                    </p>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate">{resource.body}</p>
                  </div>
                  <span
                    aria-hidden
                    className="mt-1 text-[18px] text-charcoal/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-red"
                  >
                    →
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Final CTA — cinematic close                                         */
/* ------------------------------------------------------------------ */

function FinalCtaStage() {
  return (
    <section className="relative overflow-hidden bg-charcoal text-white" data-reveal>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 75% 60% at 50% 45%, rgba(196,30,42,0.22) 0%, rgba(196,30,42,0.05) 38%, transparent 72%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)",
        }}
      />
      <FilmGrain />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 py-24 text-center md:py-32">
        <SectionEyebrow label="Don't get left behind" tone="gold" />

        <h2 className="mt-6 font-display text-[42px] font-bold leading-[1.05] tracking-tight text-cream sm:text-[60px] md:text-[72px]">
          Your competitor
          <br />
          is booking more
          <br />
          viewings
          <span className="text-red">.</span>
        </h2>

        <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-stone/80 sm:text-base">
          The agency down the road is already filling their viewing diary with the buyers
          you&rsquo;re missing. Try it on your most expensive listing. See what happens.
        </p>

        <div className="mt-12 flex justify-center">
          <DualCta tone="dark" primaryLabel="Try it free" secondaryLabel="Book a demo" />
        </div>

        <div className="mt-20 flex flex-col items-center gap-4">
          <OrnamentRule tone="red" />
          <div className="font-display text-[18px] font-bold tracking-tight">
            <span className="text-cream">Quite</span>
            <span className="text-red">Right.</span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function Home({ meta }: { meta: SeoMeta }) {
  useEditorialReveal()

  return (
    <PublicLayout transparentOverHero>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "QuiteRight",
          url: "https://www.quiteright.co.uk",
          description: "Property video marketing platform for UK estate agents. Turn any listing into a scroll-stopping video ad within minutes.",
          logo: {
            "@type": "ImageObject",
            url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
            width: 512,
            height: 512,
          },
          image: "https://www.quiteright.co.uk/og-image.jpg",
          email: "team@quiteright.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Bristol",
            addressRegion: "South West England",
            addressCountry: "GB",
          },
          areaServed: {
            "@type": "Country",
            name: "United Kingdom",
          },
          knowsAbout: [
            "Property Video Marketing",
            "Estate Agent Marketing",
            "Property Listing Videos",
            "Social Media Marketing for Estate Agents",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            email: "team@quiteright.co.uk",
            contactType: "customer support",
            availableLanguage: "English",
          },
        })}</script>

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "QuiteRight",
          url: meta.canonical,
          description: meta.description,
        })}</script>
      </Head>

      <EditorialStyles />
      <ScrollProgress />

      <HeroStage example={HERO_EXAMPLE} />
      <StatStrip />
      <PainSection />
      <DistributionStage example={HERO_EXAMPLE} />
      <HowItWorksSection />
      <AdvantagesSection />
      <MidCtaStage />
      <ObjectionsSection />
      <AudiencesSection />
      <PortalsStrip />
      <ResourcesSection />
      <FinalCtaStage />
    </PublicLayout>
  )
}
